import React, { useState } from 'react';
import { get } from 'lodash';
import { getIn } from 'formik';
import { faBriefcase } from '@fortawesome/free-solid-svg-icons/faBriefcase';
import { faLightbulb } from '@fortawesome/free-solid-svg-icons/faLightbulb';
import { faLocationArrow } from '@fortawesome/free-solid-svg-icons/faLocationArrow';
import { faUsers } from '@fortawesome/free-solid-svg-icons/faUsers';
import { faUsersLine } from '@fortawesome/free-solid-svg-icons/faUsersLine';

import ErrorBoundary from 'buyside/components/presentation/ErrorBoundary/errorBoundary';
import LoadingSpinner from 'buyside/components/presentation/LoadingSpinner/loadingSpinner';
import UserProfileBanner from './UserProfileBanner/userProfileBanner';
import UserProfileAdminDashboard from './UserProfileAdminDashboard/userProfileAdminDashboard';
import PageNavigator from 'buyside/components/presentation/PageNavigator/pageNavigator';
import UserProfileSection from './UserProfileSection/userProfileSection';
import ProfileHeader, {
  PROFILE_TYPES
} from 'buyside/components/presentation/ProfileHeader/profileHeader';

import {
  employerProfileUrl,
  profileSearchUrl,
  userFollowersUrl,
  userFollowingUrl,
  userIdeaUrl
} from 'buyside/util/urlHelper';

import styles from './userProfileShowPageStyles.scss';

import {
  SECTIONS,
  ICONS,
  TITLES,
  MINIMUM_ATTRIBUTES,
  EMPTY_TEXT,
  VISIBILITIES,
  COMPONENTS,
  EDIT_FORMS,
  EDIT_FORM_BOX_TEXT,
  VALIDATION_SCHEMATA,
  COMMUNITY,
  HIRING_MANAGER,
  ADMIN
} from 'buyside/util/userProfileConstants';

export const accreditedRetailUserBlocklist = [
  'research_performance',
  'awards',
  'expertise',
  'academic_performance',
  'research',
  'q_and_a'
];

const UserProfileShowPage = ({
  loading,
  currentUser,
  userProfile,
  updateBackgroundImage,
  createMessage,
  updateUserProfile
}) => {
  const [viewAsMode, setViewAsMode] = useState(false);
  const [viewingAs, setViewingAs] = useState(COMMUNITY);

  const toggleViewAsMode = () => setViewAsMode(!viewAsMode);
  const viewAs = userType => setViewingAs(userType);

  let editModalOpeners;

  const addEditModalOpener = (sectionRef, sectionName) => {
    if (sectionRef) {
      editModalOpeners = {
        ...editModalOpeners,
        [sectionName]: sectionRef.openEditModal
      };
    }
  };

  if (loading) {
    return <LoadingSpinner size="large" />;
  }

  const {
    isAdmin,
    isUserProfileAdmin,
    isHiringManager,
    canViewUserDetails,
    canManageMessages
  } = currentUser;
  const {
    certifications,
    city,
    email,
    isAccredited,
    mobilePhone,
    user: {
      id: userId,
      accreditedInvestorProfile,
      activeFollowedUsersCount,
      activeFollowersCount,
      displayName,
      isRetail,
      mostRecentEmployer,
      publishedIdeasCount
    },
    workPhone
  } = userProfile;
  const employer = get(mostRecentEmployer, 'employer');
  const isCurrentEmployer = get(mostRecentEmployer, 'currentEmployer', true);

  const viewerRole = isUserProfileAdmin
    ? viewAsMode
      ? viewingAs
      : ADMIN
    : isHiringManager
    ? HIRING_MANAGER
    : COMMUNITY;
  const roleIsAdmin = viewerRole === ADMIN;
  const roleIsCommunity = viewerRole === COMMUNITY;
  const attrExists = attr => {
    const userProfileAttr = getIn(userProfile, attr);
    return Array.isArray(userProfileAttr) ? !!userProfileAttr.length : !!userProfileAttr;
  };
  const isEmpty = section => {
    if (section === 'accredited_investor_profile') {
      return !accreditedInvestorProfile;
    }
    return !MINIMUM_ATTRIBUTES[section].some(attr => attrExists(attr));
  };

  const isHidden = section => {
    return (
      (section === 'accredited_investor_profile' && !isAccredited) ||
      (!roleIsAdmin && (isEmpty(section) || VISIBILITIES[section] === ADMIN)) ||
      (roleIsCommunity && VISIBILITIES[section] === HIRING_MANAGER) ||
      (isAccredited && accreditedRetailUserBlocklist.includes(section))
    );
  };

  const showIdeas = roleIsAdmin || isHiringManager || canViewUserDetails;

  const mainDatapoints = [
    employer
      ? {
          icon: employer.hasFundProfile ? faBriefcase : null,
          title: isCurrentEmployer ? get(employer, 'name') : `Formerly At ${get(employer, 'name')}`,
          tooltip: employer.hasFundProfile
            ? "This fund has a profile on SumZero's Cap Intro platform."
            : null,
          href: employerProfileUrl(employer.id)
        }
      : null,
    city
      ? {
          icon: faLocationArrow,
          title: city,
          href: profileSearchUrl({ location: city })
        }
      : null,
    showIdeas
      ? {
          title: `${publishedIdeasCount} Ideas`,
          icon: faLightbulb,
          href: userIdeaUrl(userId)
        }
      : null,
    {
      title: `${activeFollowersCount} Followers`,
      href: userFollowersUrl(userId),
      icon: faUsersLine
    },
    {
      title: `Following ${activeFollowedUsersCount}`,
      href: userFollowingUrl(userId),
      icon: faUsers
    },
    certifications.length ? { tooltip: 'Certifications', title: certifications.join(', ') } : null
  ];

  const messageModalProps = canManageMessages
    ? {
        createMessage: createMessage,
        isCurrentEmployer,
        receiverEmployerName: get(employer, 'name'),
        receiverId: userId,
        receiverName: displayName
      }
    : null;

  return (
    <>
      <ProfileHeader
        displayName={displayName}
        isCurrentUser={currentUser.id === userId}
        mainDatapoints={mainDatapoints}
        messageModalProps={messageModalProps}
        profile={userProfile}
        profileImageUrl={userProfile.imageProfileUrl}
        profileType={PROFILE_TYPES.USER}
        ref={ref => addEditModalOpener(ref, 'card')}
        roleIsAdmin={roleIsAdmin}
        saveBackgroundImage={updateBackgroundImage}
        summaryHtml={userProfile.summary || null}
        updateCard={({ userProfile }) => updateUserProfile({ userProfile, section: 'card' })}
      />
      <PageNavigator
        fullWidth
        items={SECTIONS.filter(section => !isHidden(section)).map(section => ({
          label: TITLES[section],
          id: section
        }))}
      />
      {viewAsMode && (
        <div className={styles.profileBanner}>
          <UserProfileBanner
            toggleViewAsMode={toggleViewAsMode}
            viewAs={viewAs}
            viewAsMode={viewAsMode}
            viewingAs={viewingAs}
          />
        </div>
      )}
      {roleIsAdmin && !isRetail && (
        <UserProfileAdminDashboard
          email={email}
          isAdmin={isAdmin}
          mobilePhone={mobilePhone}
          openEditModal={sectionName => editModalOpeners[sectionName]()}
          toggleViewAsMode={toggleViewAsMode}
          userId={userId}
          userProfile={userProfile}
          workPhone={workPhone}
        />
      )}
      {SECTIONS.filter(section => !isHidden(section)).map((section, idx) => {
        return (
          <section key={idx} className={styles.profileSection} id={section}>
            <ErrorBoundary pageName={section}>
              <UserProfileSection
                accreditedInvestorProfile={accreditedInvestorProfile}
                component={COMPONENTS[section]}
                editForm={EDIT_FORMS[section]}
                editFormBoxText={EDIT_FORM_BOX_TEXT[section]}
                emptyText={EMPTY_TEXT[section] || EMPTY_TEXT.default}
                icon={ICONS[section]}
                isSumzeroAdmin={isAdmin}
                ref={sectionRef => addEditModalOpener(sectionRef, section)}
                roleIsAdmin={roleIsAdmin}
                section={section}
                sectionEmpty={isEmpty(section)}
                sectionVisibility={VISIBILITIES[section]}
                title={TITLES[section]}
                updateUserProfile={updateUserProfile}
                userProfile={userProfile}
                validationSchema={VALIDATION_SCHEMATA[section]}
              />
            </ErrorBoundary>
          </section>
        );
      })}
    </>
  );
};

export default UserProfileShowPage;
